import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material'
import TagManager from 'react-gtm-module'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { SingleItemAutocomplete } from '~components/autocomplete/single-item-autocomplete'
import { ExactScopeName, useHasScope } from '~hooks/permissions/use-has-scope'
import { useGetMe } from '~hooks/queries/me/use-get-me'
import { useGetVessels } from '~hooks/queries/vessels/use-get-vessels'
import { FavoritesSelect } from '~pages/pages-behind-login/onboarded-vessel/ops-dashboard-page/components/favourites-select'
import { constructTramosCode } from '~pages/pages-behind-login/technical-management/components/vessels-select'
import { replaceStringInPathname } from '../../utils'

interface VesselOption {
  vesselName: string
  IMONumber: string
}

interface VesselNavTarget {
  vesselId: string
  vesselCode: string
}

export const VESSEL_SWITCH_TRACKS = [
  'commercial-management',
  'pool',
  'outside-pool',
]

export function AutoCompleteAndFavorites() {
  const { data: me } = useGetMe()
  const isMtUser = useHasScope(ExactScopeName.maerskTankersUser)
  const hasFavoriteVessels =
    me?.favoriteVesselsNames && !!me.favoriteVesselsNames.length

  if (!isMtUser)
    return (
      <Box sx={{ display: 'flex', mb: 3 }}>
        <MyVesselsSelect />
      </Box>
    )

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: hasFavoriteVessels ? 'space-between' : 'flex-start',
        mb: 3,
        gap: 3,
        flexWrap: 'wrap',
      }}
    >
      <SwitchToVessel />
      {hasFavoriteVessels && <FavoritesSelect />}
    </Box>
  )
}

export function useVesselNavigation() {
  const { vesselId, vessel } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  return (target: VesselNavTarget) => {
    if (vesselId) {
      const newPath = replaceStringInPathname(vesselId, target.vesselId)
      if (newPath) navigate(newPath)
      return
    }

    if (vessel) {
      const newPath = replaceStringInPathname(
        vessel,
        constructTramosCode(target.vesselCode),
      )
      if (newPath) {
        const newParams = new URLSearchParams(searchParams)
        newParams.set('vesselId', target.vesselId)
        navigate(`${newPath}?${newParams.toString()}`)
      }
    }
  }
}

function getOptionLabel(option: string | VesselOption) {
  if (typeof option === 'string') return option
  return `${option.IMONumber} ${option.vesselName}`
}

function SwitchToVessel() {
  const { vesselId, vessel } = useParams()
  const { data: vessels } = useGetVessels()
  const navigateToVessel = useVesselNavigation()

  if (!vessels) return null

  const vesselOptions: VesselOption[] = vessels
    .filter((v) => {
      if (vesselId) return v.vesselId !== vesselId
      if (vessel) return constructTramosCode(v.vesselCode) !== vessel
      return true
    })
    .map((vessel) => ({
      vesselName: vessel.vesselName,
      IMONumber: vessel.imoNumber,
    }))

  return (
    <SingleItemAutocomplete
      key={vesselId ?? vessel}
      className="tour-step-vessel-switch"
      input={null}
      freeSolo={false}
      sx={{ minWidth: 300 }}
      label="Switch to another vessel"
      options={vesselOptions}
      getOptionLabel={getOptionLabel}
      onChangeHandler={(_, newValue) => {
        TagManager.dataLayer({ dataLayer: { event: 'vessel_searched' } })

        if (!newValue || typeof newValue === 'string') return

        const selectedVessel = vessels.find(
          (v) => v.imoNumber === newValue.IMONumber,
        )
        if (selectedVessel) navigateToVessel(selectedVessel)
      }}
    />
  )
}

function MyVesselsSelect() {
  const { vessel: vesselCodeParam, vesselId: vesselIdParam } = useParams()
  const { data: vessels } = useGetVessels({ track: 'all' })
  const navigateToVessel = useVesselNavigation()

  if (!vessels || vessels.length === 0) return null

  const currentVessel = vessels.find((v) => {
    if (vesselIdParam) return v.vesselId === vesselIdParam
    if (vesselCodeParam)
      return constructTramosCode(v.vesselCode) === vesselCodeParam
    return false
  })

  return (
    <FormControl className="tour-step-vessel-switch" size="small">
      <InputLabel id="my-vessels-select-label">My vessels</InputLabel>
      <Select
        sx={{ minWidth: 300 }}
        labelId="my-vessels-select-label"
        label="My vessels"
        value={currentVessel?.vesselId || ''}
        onChange={(e) => {
          TagManager.dataLayer({
            dataLayer: {
              event: 'my_vessel_searched',
            },
          })

          const selectedVessel = vessels.find(
            (v) => v.vesselId === e.target.value,
          )
          if (selectedVessel) {
            navigateToVessel(selectedVessel)
          }
        }}
      >
        {vessels.map((vessel) => (
          <MenuItem key={vessel.vesselId} value={vessel.vesselId}>
            {vessel.imoNumber} {vessel.vesselName}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}
